import { Activity, BarChart3, Database, MapPin } from 'lucide-react'
import { RATE_LABELS, useLiveRates, useProviderStatus } from '@/services/marketData'

/**
 * Market Intel on the live partition.
 *
 * The page used to render the illustrative dataset to every account: CoStar,
 * REIS, Cushman & Wakefield and Trepp figures under a green "4 feeds active",
 * Bloomberg/Moody's/S&P model outputs, an Austin neighbourhood profile, a
 * "Placer.ai-style" foot-traffic panel and a rate history drawn from sine
 * waves — all under "LIVE FEED", beside "SOFR 0%" because the one real feed
 * the platform has (GET /integrations/rates) was never called (EXEC-02,
 * MOD-03). This renders only what is connected, and names what is not.
 */

function NotConnected({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[96px] items-center justify-center rounded-md border border-dashed border-amber-400/10 px-4 py-5 text-center text-[11px] leading-relaxed text-stone-500">
      {children}
    </div>
  )
}

function PanelHeader({ icon, title, right }: { icon: React.ReactNode; title: string; right?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 border-b border-amber-400/10 px-3 py-2">
      {icon}
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">{title}</span>
      {right && <span className="ml-auto">{right}</span>}
    </div>
  )
}

export default function MarketIntelligenceLive() {
  const rates = useLiveRates()
  const status = useProviderStatus()
  const providers = (status.data ?? []).filter((p) => p.provider !== 'mock')
  const connected = providers.filter((p) => p.configured)

  return (
    <div className="page-fill gap-2 p-3">
      {/* ── RATE STRIP: only the public feed that is actually wired ─────── */}
      <div className="market-strip flex items-center gap-6 overflow-x-auto rounded-lg px-4 py-2">
        {rates.isLoading && <span className="text-[11px] text-stone-500">Loading rates…</span>}
        {rates.isError && (
          <span className="text-[11px] text-stone-500">
            The public rates feed (NY Fed / US Treasury) did not answer. No rate is shown rather than a stale one.
          </span>
        )}
        {(rates.data ?? []).map((r) => {
          const meta = RATE_LABELS[r.indexName] ?? { label: r.indexName, source: r.provider }
          return (
            <div key={r.indexName} className="market-chip flex items-center gap-2 whitespace-nowrap rounded px-3 py-1.5">
              <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-stone-500">{meta.label}</span>
              <span className="text-sm font-semibold text-amber-300">{(r.rate * 100).toFixed(2)}%</span>
              <span className="text-[10px] text-stone-600">
                {meta.source}
                {r.asOf ? ` · as of ${r.asOf}` : ''}
              </span>
            </div>
          )
        })}
        {rates.data && rates.data.length === 0 && (
          <span className="text-[11px] text-stone-500">The rates feed answered with no quotes.</span>
        )}
        <div className="ml-auto whitespace-nowrap text-[10px] text-stone-600">
          10Y UST, CMBS AAA/BBB spreads: no source connected
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-2 gap-2 overflow-hidden">
        {/* ── Cap rates / model comparison: vendor data only ─────────── */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-amber-400/10 bg-black/30">
          <PanelHeader
            icon={<BarChart3 className="h-3.5 w-3.5 text-amber-400/60" />}
            title="Cap Rates & Model Comparison"
          />
          <div className="space-y-2 p-3">
            <NotConnected>
              Market cap rates by property type need a market-data provider (CoStar or REIS). Neither is connected, so
              none is shown. A deal&apos;s own stated cap rate is on its dossier.
            </NotConnected>
            <NotConnected>
              Third-party credit models (Bloomberg, Moody&apos;s, S&amp;P) are not connected, so there is no model
              comparison to show.
            </NotConnected>
          </div>
        </div>

        {/* ── Provider status, from /integrations/status ─────────────── */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-amber-400/10 bg-black/30">
          <PanelHeader
            icon={<Database className="h-3.5 w-3.5 text-amber-400/60" />}
            title="Market Data Feeds"
            right={
              status.data ? (
                <span className="text-[9px] text-stone-400">
                  {connected.length} of {providers.length} connected
                </span>
              ) : null
            }
          />
          <div className="min-h-0 flex-1 overflow-y-auto p-3 scrollbar-thin">
            {status.isLoading && <p className="text-[11px] text-stone-500">Loading provider status…</p>}
            {status.isError && (
              <NotConnected>Provider status could not be loaded. Settings → Integrations lists every provider.</NotConnected>
            )}
            {status.data && (
              <table className="dense-table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Supplies</th>
                    <th className="text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {providers.map((p) => (
                    <tr key={p.provider}>
                      <td className="font-medium text-stone-300">{p.displayName}</td>
                      <td className="text-stone-500">{p.capabilities.join(', ').replace(/_/g, ' ')}</td>
                      <td className={`text-right ${p.configured ? 'text-emerald-400' : 'text-stone-500'}`}>
                        {p.configured ? 'Connected' : 'Not connected'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* ── Neighbourhood / foot traffic / rate history ──────────────── */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg border border-amber-400/10 bg-black/30">
          <PanelHeader icon={<MapPin className="h-3.5 w-3.5 text-amber-400/60" />} title="Neighborhood Intel" />
          <div className="p-3">
            <NotConnected>
              No submarket data source is connected. Location intelligence for a specific deal (census demographics,
              nearby places) is on that deal&apos;s dossier.
            </NotConnected>
          </div>
        </div>
        <div className="rounded-lg border border-amber-400/10 bg-black/30">
          <PanelHeader icon={<Activity className="h-3.5 w-3.5 text-amber-400/60" />} title="Foot Traffic" />
          <div className="p-3">
            <NotConnected>No foot-traffic or alternative-data provider is connected.</NotConnected>
          </div>
        </div>
        <div className="rounded-lg border border-amber-400/10 bg-black/30">
          <PanelHeader icon={<Activity className="h-3.5 w-3.5 text-amber-400/60" />} title="Rate Trends" />
          <div className="p-3">
            <NotConnected>
              Rate history is not stored yet; the strip above shows the latest published print for each index.
            </NotConnected>
          </div>
        </div>
      </div>
    </div>
  )
}
